// =============================
// IMPORTS (DEVONO STARE PER PRIMI)
// =============================
import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useEffect, useMemo, useState } from "react";
import { caricaMenuLocale, caricaPreferenzeAI } from "../api/aiService";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { defaultUIStrings } from "../uiStrings";
import "./aiCliente.css";

// =============================
// SYSTEM PROMPT DINAMICO AI
// =============================
function generaSystemPrompt(localeName, menu, preferenze) {

  const piattiSpingere = preferenze.piatti_da_spingere || [];

  return `
SEI L'ASSISTENTE AI DEL RISTORANTE: "${localeName}".

=========================================
⭐ PIATTI DA SPINGERE (PRIORITÀ)
=========================================
${
  piattiSpingere.length > 0
    ? piattiSpingere.map(p => `- ${p.nome}`).join("\n")
    : "Nessun piatto speciale selezionato."
}

=========================================
📌 MENU DEL LOCALE
=========================================
${
  menu.map(p => {
    const nome = typeof p.nome === "string" ? p.nome
      : (p.nome?.it || p.nome?.en || Object.values(p.nome || {})[0] || "");
    const cat = typeof p.categoria === "string" ? p.categoria
      : (p.categoria?.it || p.categoria?.en || Object.values(p.categoria || {})[0] || "");
    return `- ${nome} — Categoria: ${cat}`;
  }).join("\n")
}

=========================================
📌 PREFERENZE DEL GESTORE
=========================================
🎯 Obiettivo: ${preferenze.obiettivo}
🗣️ Tono: ${preferenze.tono}
🔥 Intensità: ${preferenze.intensita}
🌍 Lingua: ${preferenze.lingua}

─────────────────────────────────────────
ISTRUZIONI
─────────────────────────────────────────
1. Rispondi solo riguardo ai piatti del menu.
2. Se la domanda è fuori tema → "Posso aiutarti solo con i piatti del nostro menu 😊"
3. Non inventare nulla.
4. Suggerisci max 3 piatti.
5. Dai priorità ai piatti da spingere quando compatibili.
6. Mantieni SEMPRE la lingua impostata dal gestore.

─────────────────────────────────────────
RISPOSTA
Comportati come un cameriere AI professionale.
`;
}

// src/ai/AICliente.js
// Assistente AI lato cliente (fullscreen) – consigli rapidi + abbinamenti

function AICliente({ localeTag, menu = [], lingua = "it", piattoSelezionato = null, openFromNavbar = false, onClose = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [uiTranslations, setUiTranslations] = useState({});

  // Sincronizza apertura da navbar
  useEffect(() => {
    if (openFromNavbar) {
      setIsOpen(true);
      setStep("pills");
    }
  }, [openFromNavbar]);

  // Carica traduzioni UI da Firestore
  useEffect(() => {
    const fetchUITranslations = async () => {
      if (!localeTag) return;
      try {
        const uiTransRef = doc(db, "restaurants", localeTag, "ai", "uiTranslations");
        const uiTransSnap = await getDoc(uiTransRef);
        if (uiTransSnap.exists()) {
          setUiTranslations(uiTransSnap.data());
        }
      } catch (err) {
        console.error("Errore caricamento traduzioni UI:", err);
      }
    };
    fetchUITranslations();
  }, [localeTag]);

  // Stato AI
  const [preferenzeAI, setPreferenzeAI] = useState(null);
  const [scenarioAttivo, setScenarioAttivo] = useState(null);
  const [loadingPrefs, setLoadingPrefs] = useState(false);

  const [loadingConsigliRapidi, setLoadingConsigliRapidi] = useState(false);
  const [loadingUpsell, setLoadingUpsell] = useState(false);

  const [errore, setErrore] = useState("");
  const [consigliRapidi, setConsigliRapidi] = useState(null);
  const [risultatoUpsell, setRisultatoUpsell] = useState(null);
  const [piattoSceltoPerAbbinamenti, setPiattoSceltoPerAbbinamenti] = useState(null);
  // Step del flusso: "pills" | "consigli" | "abbinamenti"
  const [step, setStep] = useState("pills");
  const [customRequest, setCustomRequest] = useState("");
  const piattiDisponibili = useMemo(() => {
    if (!Array.isArray(menu)) return [];

    const nomeLower = (p) => {
      if (!p) return "";
      if (typeof p.nome === "string") return p.nome.toLowerCase();
      if (p.nome?.[lingua]) return String(p.nome[lingua]).toLowerCase();
      if (p.nome?.it) return String(p.nome.it).toLowerCase();
      return "";
    };

    const score = (p) => {
      const cat = (p?.categoria || "").toLowerCase();
      const nome = nomeLower(p);
      let s = 0;
      const isBevanda =
        cat.includes("bev") ||
        cat.includes("drink") ||
        nome.includes("acqua") ||
        nome.includes("water") ||
        nome.includes("bibita") ||
        nome.includes("bevanda");
      if (isBevanda) s -= 10;
      if (p?.prezzo) s += 1;
      return s;
    };

    return [...menu].sort((a, b) => score(b) - score(a));
  }, [menu, lingua]);

  const piattoPerAbbinamenti = useMemo(() => {
    if (piattoSceltoPerAbbinamenti) return piattoSceltoPerAbbinamenti;
    if (piattoSelezionato) return piattoSelezionato;
    if (piattiDisponibili.length > 0) return piattiDisponibili[0];
    return null;
  }, [piattoSceltoPerAbbinamenti, piattoSelezionato, piattiDisponibili]);

  // Helper per estrarre nome piatto (gestisce string o object)
  const getNomePiatto = (piatto) => {
    if (!piatto) return "";
    if (typeof piatto.nome === "string") return piatto.nome;
    if (piatto.nome?.[lingua]) return piatto.nome[lingua];
    if (piatto.nome?.it) return piatto.nome.it;
    return "";
  };

  // Lookup traduzioni UI con fallback per chiavi mancanti
  const TT = useMemo(() => {
    return {
      ...defaultUIStrings,
      ...(uiTranslations[lingua] || {}),
    };
  }, [uiTranslations, lingua]);

  /* ----------------------------------------------------------
  🔹 Carica preferenze AI del locale
  ---------------------------------------------------------- */
  useEffect(() => {
    const loadPrefs = async () => {
      if (!isOpen) return;
      if (!localeTag) return;

      try {
        setLoadingPrefs(true);
        const prefs = await caricaPreferenzeAI(localeTag);
        setPreferenzeAI(prefs || null);
      } catch (err) {
        console.error("Errore caricamento preferenze AI:", err);
      } finally {
        setLoadingPrefs(false);
      }
    };

    loadPrefs();
  }, [isOpen, localeTag]);

  /* ----------------------------------------------------------
  🔹 Consigli rapidi (preset)
  ---------------------------------------------------------- */
  const scenariRapidi = [
    {
      id: "leggero",
      titolo: TT.aiLightTitle,
      sottotitolo: TT.aiLightSubtitle,
      umore: "leggero",
      nota: "Consiglia piatti leggeri e digeribili, niente fritto.",
      icona: "🥗",
    },
    {
      id: "affamato",
      titolo: TT.aiHungryTitle,
      sottotitolo: TT.aiHungrySubtitle,
      umore: "molto affamato",
      nota: "Preferisci porzioni abbondanti e ricche.",
      icona: "🍽️",
    },
    {
      id: "romantico",
      titolo: TT.aiSpecialTitle,
      sottotitolo: TT.aiSpecialSubtitle,
      umore: "romantico",
      nota: "Suggerisci piatti ideali per una cena romantica.",
      icona: "❤️",
    },
    {
      id: "felice",
      titolo: TT.aiHappyTitle,
      sottotitolo: TT.aiHappySubtitle,
      umore: "felice",
      nota: "Suggerisci piatti gustosi, colorati, soddisfacenti.",
      icona: "😊"
    },
    {
      id: "stressato",
      titolo: TT.aiStressedTitle,
      sottotitolo: TT.aiStressedSubtitle,
      umore: "stressato",
      nota: "Preferire piatti caldi e rilassanti.",
      icona: "😫"
    },
    {
      id: "triste",
      titolo: TT.aiSadTitle,
      sottotitolo: TT.aiSadSubtitle,
      umore: "triste",
      nota: "Proponi comfort food e piatti coccolosi.",
      icona: "😔"
    },
    {
      id: "arrabbiato",
      titolo: TT.aiAngryTitle,
      sottotitolo: TT.aiAngrySubtitle,
      umore: "arrabbiato",
      nota: "Suggerisci piatti intensi o speziati.",
      icona: "😡"
    },
    {
      id: "curioso",
      titolo: TT.aiCuriousTitle,
      sottotitolo: TT.aiCuriousSubtitle,
      umore: "curioso",
      nota: "Suggerisci piatti particolari o specialità uniche.",
      icona: "🤔"
    },
  ];

  

  const handleAbbinamenti = async () => {
    try {
      if (!piattoPerAbbinamenti) {
        setErrore("Nessun piatto disponibile per abbinamenti.");
        return;
      }

      setErrore("");
      setLoadingUpsell(true);
      setRisultatoUpsell(null);

      const menuLocale = await caricaMenuLocale(localeTag);

      const functions = getFunctions();
      const fn = httpsCallable(functions, "assistenteAbbinamentiAI");

      const res = await fn({
        localeTag,
        menu: menuLocale,
        piattoBase: piattoPerAbbinamenti,
        scenario: scenarioAttivo,
        preferenze: preferenzeAI
      });

      const { upsell, crossSell } = res.data || {};

      if (!upsell && !crossSell) {
        throw new Error("Nessun abbinamento restituito");
      }

      setRisultatoUpsell({
        upselling: upsell,
        cross_selling: crossSell
      });

    } catch (err) {
      console.error("ERRORE ABBINAMENTI AI:", err);
      setErrore("Errore durante gli abbinamenti.");
    } finally {
      setLoadingUpsell(false);
    }
  };


  const handleConsiglioRapido = async (scenario) => {
    try {
      if (
  scenario?.id === "custom" &&
  !customRequest.trim()
) {
  setErrore("Scrivi qualcosa per ricevere un consiglio personalizzato.");
  return;
}

      if (!piattiDisponibili.length) {
        setErrore("Menu non disponibile in questo momento.");
        return;
      }

      // Subito: mostra solo la pill selezionata + animazione "ragionando"
      setScenarioAttivo(scenario.id);
      setStep("thinking");
      setErrore("");
      setLoadingConsigliRapidi(true);
      setConsigliRapidi(null);

      const menuLocale = await caricaMenuLocale(localeTag);

      const preferenze = (await caricaPreferenzeAI(localeTag)) || {
        obiettivo: "equilibrato",
        tono: "normale",
        intensita: "moderata",
        lingua: "italiano",
        piatti_da_spingere: []
      };

      const piattiDaSpingere = preferenze.piatti_da_spingere || [];

      const functions = getFunctions();
      const fn = httpsCallable(functions, "assistenteAI");

      const res = await fn({
        localeTag,
        menu: menuLocale,
        preferenze,
        piattiDaSpingere,
        scenario: scenario?.id || "consiglio_rapido",
        messaggio: `
L'utente chiede un consiglio rapido.

UMORE:
"${scenario.titolo}"

RICHIESTA AGGIUNTIVA UTENTE:
"${customRequest || "Nessuna richiesta aggiuntiva"}"
Piatti prioritari: ${JSON.stringify(piattiDaSpingere?.map(p => p.nome) || [])}

Rispondi SOLO con JSON valido senza markdown:
{"suggerimenti":[{"nome":"Nome esatto dal menu","motivo":"Motivo breve max 12 parole"},{"nome":"...","motivo":"..."}]}
Suggerisci 2-3 piatti con nomi ESATTI del menu.
`
      });

      const risposta =
        res?.data?.risposta ||
        res?.data?.result ||
        (typeof res?.data === "string" ? res.data : "");

      if (!risposta || !risposta.trim()) {
        throw new Error("AI non ha risposto correttamente.");
      }

      // Parsing robusto: JSON prima, fallback testuale
      let suggeriti = [];
      try {
        const clean = risposta.replace(/```json|```/g, "").trim();
        const obj = JSON.parse(clean);
        suggeriti = (obj.suggerimenti || []).filter(p => p.nome).slice(0, 3);
      } catch {
        const lines = risposta.split("\n").map(l => l.trim()).filter(l => l.length > 0);
        suggeriti = lines.map((line) => {
          const parts = line.split("–");
          const fallbackParts = line.split("-");
          const [nomeRaw, motivoRaw] =
            parts.length > 1 ? parts : (fallbackParts.length > 1 ? fallbackParts : [line, ""]);
          return { nome: (nomeRaw || "").trim(), motivo: (motivoRaw || "").trim() };
        }).filter(p => p.nome.length > 1).slice(0, 3);
      }

      setConsigliRapidi({ consigliati: suggeriti });
      setStep("consigli");

    } catch (err) {
      console.error("ERRORE AI CONSIGLI RAPIDI:", err);
      setErrore(TT.aiQuickAdviceError);
    } finally {
      setLoadingConsigliRapidi(false);
    }
  };


    const handleClickSuggerito = async (piatto) => {
      try {
        setErrore("");
        setLoadingUpsell(true);
        setRisultatoUpsell(null);


        // trova il piatto reale nel menu (versione normalizzata)
        const normalize = (str) =>
          (str || "")
            .toLowerCase()
            .trim()
            .replace(/\s+/g, " ");

        const piattoMatch = menu.find((m) => {
          const nomeMenu =
            typeof m.nome === "string"
              ? m.nome
              : m.nome?.[lingua] || m.nome?.it;

          return normalize(nomeMenu) === normalize(piatto.nome);
        });

        if (!piattoMatch) {
          setErrore("Piatto non trovato nel menu.");
          return;
        }

        setPiattoSceltoPerAbbinamenti(piattoMatch);
        setStep("thinking-abbinamenti");  // mostra subito animazione
        setRisultatoUpsell(null);

        const menuLocale = await caricaMenuLocale(localeTag);

        const functions = getFunctions();
        const fn = httpsCallable(functions, "assistenteAbbinamentiAI");

        const res = await fn({
          localeTag,
          menu: menuLocale,
          piattoBase: piattoMatch,
          scenario: scenarioAttivo, // 💥 QUI PASSI IL MOOD
          preferenze: preferenzeAI // 💥 QUI PASSI STRATEGIA GESTORE
        });

        const { suggerimenti, upsell, crossSell } = res.data || {};

        // Supporta sia array suggerimenti che vecchio formato
        const suggsArr = Array.isArray(suggerimenti) && suggerimenti.length
          ? suggerimenti
          : [upsell, crossSell].filter(Boolean);

        setRisultatoUpsell({ suggerimenti: suggsArr });
        setStep("abbinamenti");

      } catch (err) {
        console.error(err);
        setErrore("Errore durante il suggerimento avanzato.");
        setStep("consigli");
      } finally {
        setLoadingUpsell(false);
      }
    };
  const themeClass = "dark";

  return (
    <>

      {isOpen && (
        <>
          <div className={`ai-client-overlay ${themeClass}`}>
            <div className="ai-client-panel">
              <header className="ai-client-header">
  <div className="ai-client-hero">

    <div className="ai-client-hero-orb"></div>

    <div className="ai-client-hero-top">
      <div className="ai-client-hero-badge">
        ✨ AI Concierge
      </div>

      <button
        className="ai-client-close-btn"
        onClick={() => {
          setIsOpen(false);
          setStep("pills");
          setScenarioAttivo(null);
          setConsigliRapidi(null);
          setRisultatoUpsell(null);
          setPiattoSceltoPerAbbinamenti(null);
          if (onClose) onClose();
        }}
      >
        ✕
      </button>
    </div>

    <div className="ai-client-hero-row">

  <div className="ai-client-header-icon-wrap">
    <span className="ai-client-header-icon">
      🤖
    </span>
  </div>

  <div className="ai-client-hero-text">
    <h1 className="ai-client-hero-title">
      {TT.aiAssistantTitle}
    </h1>

    <p className="ai-client-hero-subtitle">
      Dimmi come ti senti e ti consiglierò il piatto perfetto.
    </p>
  </div>

</div>

  </div>
</header>

              <div className="ai-client-body">
                {loadingPrefs && (
                  <div className="ai-client-info">
                    {TT.aiPreparingAdvice}
                  </div>
                )}

                {errore && <div className="ai-client-error">{errore}</div>}

                {/* STEP 1 — Scegli umore */}
                {step === "pills" && (
                  <section className="ai-client-section">
                    <h3 className="ai-client-section-title">
                      {TT.aiHowDoYouFeel}
                    </h3>
                    <div className="ai-client-input-wrapper">

 <textarea
  className="ai-client-textarea"
  placeholder="Es. Vorrei qualcosa di cremoso ma senza carne…"
  value={customRequest}
  onChange={(e) => setCustomRequest(e.target.value)}
/>

  
<button
  className="ai-client-generate-btn"
  onClick={() =>
    handleConsiglioRapido({
      id: "custom",
      titolo: customRequest || "Richiesta personalizzata",
      sottotitolo: "Consiglio AI personalizzato",
      icona: "✨"
    })
  }
>
  ✨ Consigliami qualcosa
</button>

</div>
                    <div className="ai-client-quick-grid">
                      {scenariRapidi.map((s) => (
                        <button
                          key={s.id}
                          className="ai-client-pill"
                          onClick={() => handleConsiglioRapido(s)}
                        >
                          <span className="ai-client-pill-icon">{s.icona}</span>
                          <div className="ai-client-pill-content">
                            <div className="ai-client-pill-title">{s.titolo}</div>
                            <div className="ai-client-pill-subtitle">{s.sottotitolo}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {/* STEP THINKING — pill selezionata + animazione ragionamento */}
                {step === "thinking" && (
                  <section className="ai-client-section">
                    {scenariRapidi.filter(s => s.id === scenarioAttivo).map((s) => (
                      <div key={s.id} className="ai-client-pill active ai-client-pill-solo" style={{ marginBottom: "1.2rem" }}>
                        <span className="ai-client-pill-icon">{s.icona}</span>
                        <div className="ai-client-pill-content">
                          <div className="ai-client-pill-title">{s.titolo}</div>
                          <div className="ai-client-pill-subtitle">{s.sottotitolo}</div>
                        </div>
                      </div>
                    ))}
                    <div className="ai-client-thinking">
                      <div className="ai-thinking-dots">
                        <span /><span /><span />
                      </div>
                      <p className="ai-thinking-text">Sto ragionando sul consiglio perfetto per te…</p>
                    </div>
                  </section>
                )}

                {/* STEP 2 — Consigli ricevuti, scegli un piatto */}
                {step === "consigli" && (
                  <section className="ai-client-section">
                    {/* Pill attiva — cliccabile per tornare */}
                    {scenariRapidi.filter(s => s.id === scenarioAttivo).map((s) => (
                      <button
                        key={s.id}
                        className="ai-client-pill active ai-client-pill-solo"
                        style={{ marginBottom: "1rem" }}
                        onClick={() => {
                          setStep("pills");
                          setConsigliRapidi(null);
                          setScenarioAttivo(null);
                          setRisultatoUpsell(null);
                          setPiattoSceltoPerAbbinamenti(null);
                        }}
                      >
                        <span className="ai-client-pill-icon">{s.icona}</span>
                        <div className="ai-client-pill-content">
                          <div className="ai-client-pill-title">{s.titolo}</div>
                          <div className="ai-client-pill-subtitle">{s.sottotitolo}</div>
                        </div>
                      </button>
                    ))}

                    {/* Invito al click */}
                    <p className="ai-client-invite-text">
                      👆 Tocca un piatto per vedere gli abbinamenti consigliati
                    </p>

                    {consigliRapidi?.consigliati?.length > 0 && (
                      <div className="ai-client-suggestions">
                        {consigliRapidi.consigliati.map((p, idx) => (
                          <div
                            key={idx}
                            className="ai-client-suggestion-card ai-client-suggestion-card--tappable"
                            onClick={() => handleClickSuggerito(p)}
                          >
                            <div className="ai-client-suggestion-name">{p.nome}</div>
                            {p.motivo && <div className="ai-client-reason">{p.motivo}</div>}
                            <div className="ai-client-tap-hint">Tocca per gli abbinamenti →</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* STEP THINKING ABBINAMENTI — piatto scelto + animazione */}
                {step === "thinking-abbinamenti" && (
                  <section className="ai-client-section">
                    {piattoSceltoPerAbbinamenti && (
                      <div className="ai-client-suggestion-card" style={{ marginBottom: "1.2rem" }}>
                        <div className="ai-client-suggestion-name">
                          {getNomePiatto(piattoSceltoPerAbbinamenti)}
                        </div>
                      </div>
                    )}
                    <div className="ai-client-thinking">
                      <div className="ai-thinking-dots">
                        <span /><span /><span />
                      </div>
                      <p className="ai-thinking-text">Sto cercando gli abbinamenti migliori…</p>
                    </div>
                  </section>
                )}

                {/* STEP 3 — Abbinamenti */}
                {step === "abbinamenti" && (
                  <section className="ai-client-section">
                    <h3 className="ai-client-section-title">{TT.aiPairingsFor}</h3>

                    <div style={{ display: "flex", gap: "0.8rem", marginBottom: "0.8rem" }}>
                      <button onClick={() => { setStep("consigli"); setRisultatoUpsell(null); setPiattoSceltoPerAbbinamenti(null); }}
                        style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", cursor: "pointer", padding: 0 }}>
                        ← Consigli
                      </button>
                      <button onClick={() => { setStep("pills"); setRisultatoUpsell(null); setPiattoSceltoPerAbbinamenti(null); setConsigliRapidi(null); setScenarioAttivo(null); }}
                        style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: "0.85rem", cursor: "pointer", padding: 0 }}>
                        ⌂ Inizio
                      </button>
                    </div>

                    {/* Piatto scelto — cliccabile per tornare */}
                    {piattoSceltoPerAbbinamenti && (
                      <div
                        className="ai-client-suggestion-card"
                        style={{ marginBottom: "1rem", cursor: "pointer" }}
                        onClick={() => {
                          setStep("consigli");
                          setRisultatoUpsell(null);
                          setPiattoSceltoPerAbbinamenti(null);
                        }}
                      >
                        <div className="ai-client-suggestion-name">
                          ← {getNomePiatto(piattoSceltoPerAbbinamenti)}
                        </div>
                      </div>
                    )}

                    {risultatoUpsell?.suggerimenti?.length > 0 && (
                      <div className="ai-client-upsell-wrapper">
                        {risultatoUpsell.suggerimenti.map((s, idx) => (
                          <div key={idx} className="ai-client-upsell-card">
                            <div className="ai-client-suggestion-name">{s.nome}</div>
                            {s.motivo && <div className="ai-client-reason">{s.motivo}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            </div>
          </div>
          <button
            className="ai-close-fab"
            onClick={() => {
              setIsOpen(false);
              setStep("pills");
              setScenarioAttivo(null);
              setConsigliRapidi(null);
              setRisultatoUpsell(null);
              setPiattoSceltoPerAbbinamenti(null);
              if (onClose) onClose();
            }}
          >
            ✕
          </button>
        </>
      )}
    </>
  );
}

export default AICliente;
